import type { ReactNode } from "react";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { StarIcon } from "@phosphor-icons/react/Star";
import { TrashIcon } from "@phosphor-icons/react/Trash";
import { WarningCircleIcon } from "@phosphor-icons/react/WarningCircle";
import { WarningIcon } from "@phosphor-icons/react/Warning";
import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { CardBrandIcon } from "@freightsimple/ui-core/Display/CardBrandIcon";
import { type Column, Table } from "@freightsimple/ui-core/Table/Table";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";
import type { BankAccount } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccount";
import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import {
  isCreditCardExpired,
  isCreditCardExpiringSoon,
} from "@/Helpers/creditCardExpiry";

interface CreditCardsPanelProps {
  creditCards: Array<CreditCard>;
  bankAccounts: Array<BankAccount>;
  setDefaultCard: (stripePaymentMethodId: string) => void;
  removeCard: (stripePaymentMethodId: string) => void;
  allowRemoveCardButton: boolean;
}

// Expiry is stored as "M/YYYY"; show it zero-padded ("08/2029"). Expired cards
// read in red with a tooltip; cards inside the 3-month window get an
// "expiring soon" tooltip. The colours/tooltip are advisory — the backend guard
// is the authority for what can actually be charged (ENG-4368).
function ExpiryCell_({ expiry }: { expiry: string }) {
  const [month, year] = expiry.split("/");
  const formatted = `${(month ?? "").padStart(2, "0")}/${year ?? ""}`;

  if (isCreditCardExpired(expiry)) {
    return (
      <Row gap={1.5} align="center">
        <T color="red">{formatted}</T>
        <Tooltip content="This card has expired. Please update your payment method.">
          <Icon
            icon={WarningCircleIcon}
            size="sm"
            weight="fill"
            color="text-red"
          />
        </Tooltip>
      </Row>
    );
  }

  if (isCreditCardExpiringSoon(expiry)) {
    return (
      <Row gap={1.5} align="center">
        <T color="secondary">{formatted}</T>
        <Tooltip content="This card will expire soon. Please update your payment method.">
          <Icon
            icon={WarningIcon}
            size="sm"
            weight="fill"
            color="text-orange"
          />
        </Tooltip>
      </Row>
    );
  }

  return <T color="secondary">{formatted}</T>;
}

export function CreditCardsPanel({
  creditCards,
  bankAccounts,
  setDefaultCard,
  removeCard,
  allowRemoveCardButton,
}: CreditCardsPanelProps) {
  const defaultCreditCard = creditCards.some((cc) => cc.isDefault);
  const defaultBankAccount = bankAccounts.some((ba) => ba.isDefault);
  const showBanner = !defaultCreditCard && defaultBankAccount;

  // Per-row kebab. "Make Default" is disabled (with an explanation) when the
  // card is already default or expired; "Remove" is disabled when it's the last
  // payment method — matching the backend guards.
  function rowMenu(card: CreditCard): ReactNode {
    const expired = isCreditCardExpired(card.expiry);
    return (
      <>
        <Button
          icon={StarIcon}
          onClick={() => setDefaultCard(card.stripePaymentMethodId)}
          {...(card.isDefault
            ? { disabled: true, disabledMessage: "This is already the default" }
            : expired
              ? {
                  disabled: true,
                  disabledMessage:
                    "This card has expired, so it can't be used as the default payment method",
                }
              : {})}
        >
          Make Default
        </Button>
        <Button
          icon={TrashIcon}
          tone="danger"
          onClick={() => removeCard(card.stripePaymentMethodId)}
          {...(allowRemoveCardButton
            ? {}
            : {
                disabled: true,
                disabledMessage: "Cannot remove last payment method",
              })}
        >
          Remove Credit Card
        </Button>
      </>
    );
  }

  const columns: Column<CreditCard>[] = [
    {
      key: "brand",
      header: "Card Type",
      render: (card) => <CardBrandIcon brand={card.brand} size="lg" />,
    },
    {
      key: "cardholder",
      header: "Cardholder",
      render: (card) => <T color="secondary">{card.name}</T>,
    },
    {
      key: "expiry",
      header: "Expiry",
      render: (card) => <ExpiryCell_ expiry={card.expiry} />,
    },
    {
      key: "last4",
      header: "Last 4",
      render: (card) => <T color="secondary">Ending {card.lastFourDigits}</T>,
    },
    {
      key: "default",
      header: "",
      hug: true,
      render: (card) =>
        card.isDefault ? (
          <Badge variant="success" icon={CheckCircleIcon} label="Default" />
        ) : undefined,
    },
  ];

  return (
    <Stack gap={4}>
      {showBanner && (
        <Banner variant="warning">
          Your current default payment method is set to a bank account
        </Banner>
      )}
      <Panel fullBleed edgeToEdge>
        <Table
          columns={columns}
          data={creditCards}
          rowKey={(card) => card.stripePaymentMethodId}
          rowMenu={rowMenu}
        />
      </Panel>
    </Stack>
  );
}
